import type { AgentPhase, PresetOption } from "@/types/createAgent";
import type { CreateFormData } from "@/components/create/types";
import {
  evaluateAgentActionReadiness,
  isFrontApprovedForPhase,
  type AgentActionReadinessReason,
} from "@/lib/agent/actionReadinessGuards";
import { buildAgentTurnPlan, type AgentTurnPlan } from "@/lib/agent/turnPlan";

export interface FilterPresetsForStateParams {
  presets: PresetOption[];
  phase: AgentPhase;
  formState: Partial<CreateFormData>;
  hasFrontImage: boolean;
  hasBackImage: boolean;
  frontApproved: boolean;
}

const GUARDED_ACTION_IDS = new Set<PresetOption["actionId"]>([
  "generate_dress_front",
  "generate_dress_back",
  "modify_dress_front",
  "modify_dress_back",
  "start_virtual_try_on",
  "submit_quote",
]);

/**
 * Block reasons whose preset stays VISIBLE instead of being filtered away.
 *
 * Filtering is silent: the control disappears and the reason the guard wrote is
 * never read, which is the same silent-refusal class the guards exist to end.
 * For the free quote — the only conversion action in the funnel — the button
 * stays, and the click runs the dispatcher's readiness check, which returns the
 * guard's message and its suggested next action.
 */
const EXPLAINED_BLOCK_REASONS: ReadonlySet<AgentActionReadinessReason> =
  new Set<AgentActionReadinessReason>(["back_image_required_for_quote"]);

function getBlockedReasonForPreset(
  preset: PresetOption,
  params: FilterPresetsForStateParams
): string | null {
  const { phase, formState, hasFrontImage, hasBackImage, frontApproved } =
    params;
  const effectiveFrontApproved = isFrontApprovedForPhase({
    phase,
    hasFrontImage,
    frontApproved,
  });

  if (GUARDED_ACTION_IDS.has(preset.actionId)) {
    const readiness = evaluateAgentActionReadiness({
      actionId: preset.actionId,
      phase,
      formState,
      hasFrontImage,
      hasBackImage,
      frontApproved: effectiveFrontApproved,
    });

    if (!readiness.allowed && !EXPLAINED_BLOCK_REASONS.has(readiness.reason)) {
      return readiness.message || "Action is not currently available.";
    }
  }

  switch (preset.actionId) {
    case "continue_to_back_design":
      return hasFrontImage ? null : "Generate the front view first.";
    case "modify_dress_back":
      return effectiveFrontApproved ? null : "Approve the front design first.";
    case "set_back_prompt":
    case "set_back_style":
      return phase === "back_design" ||
        phase === "back_review" ||
        (phase === "front_review" && hasFrontImage)
        ? null
        : "Move to back design before choosing back details.";
    // The two phase rules below describe where the PHASE preset generator
    // offers a top-up, not where a credit wall can be hit — the first wall is
    // the very first Generate, before any review phase exists. A top-up the
    // agent staged as the turn's commit action is a refused priced click and
    // passes on its own provenance; nothing about buying credits depends on the
    // design being ready.
    case "buy_credits_back":
      if (preset.presetType === "commit_action") return null;
      return phase === "front_review" && hasFrontImage
        ? null
        : "Back design credits are only available after the front view.";
    case "buy_credits_modify":
      if (preset.presetType === "commit_action") return null;
      return phase === "back_review" && hasBackImage && effectiveFrontApproved
        ? null
        : "Modification credits are only available after both views are ready.";
    default:
      return null;
  }
}

export function buildPresetTurnPlanForState(
  params: FilterPresetsForStateParams
): AgentTurnPlan {
  const blockedActions: Array<{
    actionId: PresetOption["actionId"];
    reason: string;
  }> = [];
  const visiblePresets = params.presets.filter(preset => {
    const blockedReason = getBlockedReasonForPreset(preset, params);
    if (!blockedReason) return true;

    blockedActions.push({
      actionId: preset.actionId,
      reason: blockedReason,
    });
    return false;
  });

  return buildAgentTurnPlan({
    message: "",
    phase: params.phase,
    presets: visiblePresets,
    readiness: {
      status: blockedActions.length > 0 ? "not_ready" : "unknown",
      blockedActions,
    },
  });
}

/**
 * Single source of truth for gating which presets are visible in the current
 * agent state. Used by both the server-side `presetService` and the client
 * `useAgentActions` hook to keep behavior consistent.
 */
export function filterPresetsForState(
  params: FilterPresetsForStateParams
): PresetOption[] {
  return buildPresetTurnPlanForState(params).presets;
}
